import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const Orders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [filters, setFilters] = useState({ status: '', type: '', priority: '', search: '' });
    const [stats, setStats] = useState(null);
    const [editForm, setEditForm] = useState({});

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
            const response = await axios.get(`/project-orders/admin/all?${params}`);
            if (response.data.success) {
                setOrders(response.data.orders || []);
            }
        } catch (error) {
            console.error('Siparişler yüklenemedi:', error.response?.data || error.message);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const fetchStats = useCallback(async () => {
        try {
            const response = await axios.get('/project-orders/admin/stats/overview');
            if (response.data.success) setStats(response.data.stats);
        } catch (error) {
            console.error('İstatistikler yüklenemedi:', error);
        }
    }, []);

    useEffect(() => { 
        fetchOrders(); 
        fetchStats(); 
    }, [fetchOrders, fetchStats]);

    const fetchOrderDetail = async (orderId) => {
        try {
            const response = await axios.get(`/project-orders/admin/${orderId}`);
            if (response.data.success) {
                setSelectedOrder(response.data.order);
                setShowModal(true);
            }
        } catch (error) {
            alert('Sipariş detayı yüklenemedi');
        }
    };

    const handleStatusChange = async (orderId, newStatus) => {
        try {
            await axios.put(`/project-orders/admin/${orderId}/status`, { status: newStatus });
            fetchOrders(); fetchStats();
            if (selectedOrder?._id === orderId) setSelectedOrder({ ...selectedOrder, status: newStatus });
        } catch (error) {
            alert('Durum güncellenemedi');
        }
    };

    const handleUpdateOrder = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.put(`/project-orders/admin/${selectedOrder._id}`, editForm);
            if (response.data.success) {
                alert('Sipariş güncellendi');
                setShowEditModal(false);
                fetchOrders(); fetchStats();
                fetchOrderDetail(selectedOrder._id);
            }
        } catch (error) {
            alert('Güncelleme başarısız');
        }
    };

    const handleDelete = async (orderId) => {
        if (!window.confirm('Bu siparişi silmek istediğinize emin misiniz?')) return;
        try {
            await axios.delete(`/project-orders/admin/${orderId}`);
            alert('Sipariş silindi');
            fetchOrders(); fetchStats();
            setShowModal(false);
        } catch (error) {
            alert('Sipariş silinemedi');
        }
    };

    const openEditModal = () => {
        setEditForm({
            status: selectedOrder.status,
            priority: selectedOrder.priority,
            estimatedPrice: selectedOrder.estimatedPrice || 0,
            finalPrice: selectedOrder.finalPrice || 0,
            paidAmount: selectedOrder.paidAmount || 0,
            adminNotes: selectedOrder.adminNotes || '',
            nextFollowUp: selectedOrder.nextFollowUp ? selectedOrder.nextFollowUp.split('T')[0] : ''
        });
        setShowEditModal(true);
    };

    const getStatusBadge = (s) => ({
        'new': 'bg-blue-100 text-blue-800', 'contacted': 'bg-yellow-100 text-yellow-800',
        'proposal-sent': 'bg-purple-100 text-purple-800', 'negotiating': 'bg-orange-100 text-orange-800',
        'in-progress': 'bg-indigo-100 text-indigo-800', 'completed': 'bg-green-600 text-white',
        'cancelled': 'bg-red-100 text-red-800'
    }[s] || 'bg-gray-100 text-gray-800');

    const getStatusText = (s) => ({
        'new': '🆕 Yeni', 'contacted': '📞 İletişim Kuruldu', 'proposal-sent': '📄 Teklif Gönderildi',
        'negotiating': '🤝 Görüşülüyor', 'in-progress': '🚀 Devam Ediyor',
        'completed': '✅ Tamamlandı', 'cancelled': '❌ İptal'
    }[s] || s);

    const getTypeText = (t) => ({ 
        'personal': '👤 Kişisel', 
        'business': '🏢 İş', 
        'startup': '🚀 Startup',
        'student': '🎓 Öğrenci',
        'tubitak': '🔬 TÜBİTAK'
    }[t] || t);

    const getServiceText = (s) => ({
        'mobile-app': '📱 Mobil Uygulama', 'web-app': '🌐 Web Uygulaması', 'website': '💻 Web Sitesi',
        'ui-ux': '🎨 UI/UX Tasarım', 'ecommerce': '🛒 E-Ticaret', 'api': '⚙️ API Geliştirme',
        'ai-integration': '🤖 AI Entegrasyonu', 'consultation': '💡 Danışmanlık',
        'desktop-app': '🖥️ Masaüstü', 'game-dev': '🎮 Oyun', 'blockchain': '⛓️ Blockchain', 'cloud-services': '☁️ Cloud'
    }[s] || s);

    const formatCurrency = (a) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(a || 0);

    if (loading && orders.length === 0) return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Yükleniyor...</div></div>;

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">🛒 Proje Siparişleri</h1>
                <p className="text-gray-600 mt-1">Website'den gelen proje talepleri</p>
            </div>

            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg shadow"><div className="text-sm text-gray-600">Toplam</div><div className="text-2xl font-bold text-gray-900">{stats.totalOrders}</div></div>
                    <div className="bg-white p-4 rounded-lg shadow"><div className="text-sm text-gray-600">Yeni</div><div className="text-2xl font-bold text-blue-600">{stats.pendingOrders}</div></div>
                    <div className="bg-white p-4 rounded-lg shadow"><div className="text-sm text-gray-600">Devam Eden</div><div className="text-2xl font-bold text-indigo-600">{stats.inProgressOrders}</div></div>
                    <div className="bg-white p-4 rounded-lg shadow"><div className="text-sm text-gray-600">Tamamlanan</div><div className="text-2xl font-bold text-green-600">{stats.completedOrders}</div></div>
                    <div className="bg-white p-4 rounded-lg shadow border-2 border-green-200"><div className="text-sm text-gray-600">💰 Toplam Gelir</div><div className="text-lg font-bold text-green-600">{formatCurrency(stats.totalRevenue)}</div></div>
                    <div className="bg-white p-4 rounded-lg shadow border-2 border-blue-200"><div className="text-sm text-gray-600">💵 Alınan</div><div className="text-lg font-bold text-blue-600">{formatCurrency(stats.totalPaid)}</div></div>
                </div>
            )}

            <div className="bg-white p-4 rounded-lg shadow mb-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="border rounded-md px-3 py-2">
                        <option value="">Tüm Durumlar</option>
                        <option value="new">Yeni</option><option value="contacted">İletişim Kuruldu</option>
                        <option value="proposal-sent">Teklif Gönderildi</option><option value="negotiating">Görüşülüyor</option>
                        <option value="in-progress">Devam Ediyor</option><option value="completed">Tamamlandı</option><option value="cancelled">İptal</option>
                    </select>
                    <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} className="border rounded-md px-3 py-2">
                        <option value="">Tüm Türler</option>
                        <option value="personal">Kişisel</option>
                        <option value="business">İş</option>
                        <option value="startup">Startup</option>
                        <option value="student">Öğrenci</option>
                        <option value="tubitak">TÜBİTAK</option>
                    </select>
                    <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} className="border rounded-md px-3 py-2">
                        <option value="">Tüm Öncelikler</option>
                        <option value="low">Düşük</option><option value="medium">Orta</option><option value="high">Yüksek</option><option value="urgent">Acil</option>
                    </select>
                    <input type="text" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="Ara..." className="border rounded-md px-3 py-2" />
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sipariş</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Müşteri</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tür</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hizmetler</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fiyat</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlem</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {orders.map((order) => (
                            <tr key={order.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3"><div className="text-sm font-medium">{order.orderNumber}</div></td>
                                <td className="px-4 py-3">
                                    <div className="text-sm font-medium">{order.customer.name}</div>
                                    <div className="text-xs text-gray-500">{order.customer.email}</div>
                                </td>
                                <td className="px-4 py-3 text-sm">{getTypeText(order.type)}</td>
                                <td className="px-4 py-3">
                                    <div className="flex flex-wrap gap-1">
                                        {order.services.slice(0, 2).map((s, i) => <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">{getServiceText(s).split(' ')[0]}</span>)}
                                        {order.services.length > 2 && <span className="text-xs bg-gray-200 px-2 py-1 rounded">+{order.services.length - 2}</span>}
                                    </div>
                                </td>
                                <td className="px-4 py-3"><span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(order.status)}`}>{getStatusText(order.status)}</span></td>
                                <td className="px-4 py-3 text-sm">{order.finalPrice > 0 ? formatCurrency(order.finalPrice) : (order.estimatedPrice > 0 ? <span className="text-gray-400">{formatCurrency(order.estimatedPrice)}</span> : '-')}</td>
                                <td className="px-4 py-3 text-sm text-gray-500">{new Date(order.createdAt).toLocaleDateString('tr-TR')}</td>
                                <td className="px-4 py-3"><button onClick={() => fetchOrderDetail(order.id)} className="text-indigo-600 hover:text-indigo-900 text-sm">📋 Detay</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {orders.length === 0 && <div className="text-center py-12 text-gray-500">Henüz sipariş yok</div>}
            </div>


            {/* Detay Modal */}
            {showModal && selectedOrder && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-start mb-6">
                                <div>
                                    <h2 className="text-2xl font-bold">{selectedOrder.orderNumber}</h2>
                                    <p className="text-gray-600">{getTypeText(selectedOrder.type)} Projesi</p>
                                </div>
                                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h3 className="font-semibold mb-3">👤 Müşteri Bilgileri</h3>
                                    <div className="space-y-2 text-sm">
                                        <div><span className="text-gray-600">İsim:</span> <strong>{selectedOrder.customer.name}</strong></div>
                                        <div><span className="text-gray-600">Email:</span> <a href={`mailto:${selectedOrder.customer.email}`} className="text-blue-600">{selectedOrder.customer.email}</a></div>
                                        {selectedOrder.customer.phone && <div><span className="text-gray-600">Telefon:</span> <a href={`tel:${selectedOrder.customer.phone}`} className="text-blue-600">{selectedOrder.customer.phone}</a></div>}
                                        {selectedOrder.customer.company && <div><span className="text-gray-600">Şirket:</span> {selectedOrder.customer.company}</div>}
                                    </div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h3 className="font-semibold mb-3">💰 Finansal Bilgiler</h3>
                                    <div className="space-y-2 text-sm">
                                        <div><span className="text-gray-600">Bütçe:</span> {selectedOrder.budget || 'Belirtilmemiş'}</div>
                                        <div><span className="text-gray-600">Tahmini Fiyat:</span> <strong className="text-orange-600">{formatCurrency(selectedOrder.estimatedPrice)}</strong></div>
                                        <div><span className="text-gray-600">Kesin Fiyat:</span> <strong className="text-green-600">{formatCurrency(selectedOrder.finalPrice)}</strong></div>
                                        <div><span className="text-gray-600">Alınan Ödeme:</span> <strong className="text-blue-600">{formatCurrency(selectedOrder.paidAmount)}</strong></div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-6">
                                <h3 className="font-semibold mb-3">🛠️ İstenen Hizmetler</h3>
                                <div className="flex flex-wrap gap-2">
                                    {selectedOrder.services.map((s, i) => <span key={i} className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">{getServiceText(s)}</span>)}
                                </div>
                                {selectedOrder.extras && selectedOrder.extras.length > 0 && (
                                    <div className="mt-3">
                                        <span className="text-gray-600 text-sm">Ek Hizmetler: </span>
                                        {selectedOrder.extras.map((e, i) => <span key={i} className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs ml-1">{e}</span>)}
                                    </div>
                                )}
                            </div>

                            <div className="mb-6">
                                <h3 className="font-semibold mb-3">📝 Proje Açıklaması</h3>
                                <div className="bg-gray-50 p-4 rounded-lg text-sm whitespace-pre-wrap">{selectedOrder.description}</div>
                            </div>

                            {selectedOrder.adminNotes && (
                                <div className="mb-6">
                                    <h3 className="font-semibold mb-3">📌 Admin Notları</h3>
                                    <div className="bg-yellow-50 p-4 rounded-lg text-sm border border-yellow-200">{selectedOrder.adminNotes}</div>
                                </div>
                            )}

                            <div className="flex flex-wrap gap-3 pt-4 border-t">
                                <button onClick={openEditModal} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">✏️ Düzenle</button>
                                <select value={selectedOrder.status} onChange={(e) => handleStatusChange(selectedOrder._id, e.target.value)} className="border rounded-md px-3 py-2">
                                    <option value="new">🆕 Yeni</option>
                                    <option value="contacted">📞 İletişim Kuruldu</option>
                                    <option value="proposal-sent">📄 Teklif Gönderildi</option>
                                    <option value="negotiating">🤝 Görüşülüyor</option>
                                    <option value="in-progress">🚀 Devam Ediyor</option>
                                    <option value="completed">✅ Tamamlandı</option>
                                    <option value="cancelled">❌ İptal</option>
                                </select>
                                <button onClick={() => handleDelete(selectedOrder._id)} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 ml-auto">🗑️ Sil</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-lg w-full p-6">
                        <h3 className="text-xl font-bold mb-4">Sipariş Düzenle</h3>
                        <form onSubmit={handleUpdateOrder} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Durum</label>
                                    <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className="w-full border rounded-md px-3 py-2">
                                        <option value="new">Yeni</option><option value="contacted">İletişim Kuruldu</option>
                                        <option value="proposal-sent">Teklif Gönderildi</option><option value="negotiating">Görüşülüyor</option>
                                        <option value="in-progress">Devam Ediyor</option><option value="completed">Tamamlandı</option><option value="cancelled">İptal</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Öncelik</label>
                                    <select value={editForm.priority} onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })} className="w-full border rounded-md px-3 py-2">
                                        <option value="low">Düşük</option><option value="medium">Orta</option><option value="high">Yüksek</option><option value="urgent">Acil</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Tahmini Fiyat (₺)</label>
                                    <input type="number" value={editForm.estimatedPrice} onChange={(e) => setEditForm({ ...editForm, estimatedPrice: Number(e.target.value) })} className="w-full border rounded-md px-3 py-2" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Kesin Fiyat (₺)</label>
                                    <input type="number" value={editForm.finalPrice} onChange={(e) => setEditForm({ ...editForm, finalPrice: Number(e.target.value) })} className="w-full border rounded-md px-3 py-2" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Alınan (₺)</label>
                                    <input type="number" value={editForm.paidAmount} onChange={(e) => setEditForm({ ...editForm, paidAmount: Number(e.target.value) })} className="w-full border rounded-md px-3 py-2" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Sonraki Takip Tarihi</label>
                                <input type="date" value={editForm.nextFollowUp} onChange={(e) => setEditForm({ ...editForm, nextFollowUp: e.target.value })} className="w-full border rounded-md px-3 py-2" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Admin Notları</label>
                                <textarea value={editForm.adminNotes} onChange={(e) => setEditForm({ ...editForm, adminNotes: e.target.value })} rows={3} className="w-full border rounded-md px-3 py-2" />
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button type="submit" className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">💾 Kaydet</button>
                                <button type="button" onClick={() => setShowEditModal(false)} className="flex-1 px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400">İptal</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Orders;
